import { fabric } from 'fabric'
import tinycolor from 'tinycolor2'

const textPadding = 10

const newText = function (width, height, strokeWidth, settings) {
  settings.originY = 'center'
  settings.width = width - (2 * strokeWidth) - (2 * textPadding)
  settings.height = height
  settings.top = height / 2
  settings.left = strokeWidth + textPadding
  return new fabric.Textbox('', settings)
}

const fixTextContrast = function (shape, settings) {
  if (!shape.fill) {
    return
  }

  const cutoff = { light: 51, dark: 80 }
  const shapeFill = tinycolor(shape.fill)
  const textFill = tinycolor(settings.fill)

  const diff = Math.abs(shapeFill.getBrightness() - textFill.getBrightness())

  if (shapeFill.isDark() && diff < cutoff.dark) {
    settings.fill = '#FFFFFF'
  } else if (shapeFill.isLight() && diff < cutoff.light) {
    settings.fill = '#000000'
  }
}

export default {
  type: 'shapeWithText',

  isA: function (object) {
    return object.customType === this.type
  },

  isText: function (object) {
    return object.parent && object.type === 'textbox'
  },

  new: function (shape, settings) {
    const left = shape.left
    const top = shape.top
    const originX = shape.originX
    const originY = shape.originY

    shape.set({
      left: 0,
      top: 0,
      originX: 'left',
      originY: 'top'
    })

    fixTextContrast(shape, settings)

    const text = newText(shape.width, shape.height, shape.strokeWidth, settings)

    const group = new fabric.Group([shape, text], {
      customType: this.type,
      left,
      top,
      originX,
      originY,
      position: shape.position
    })

    return group
  },

  getShape: function (group) {
    return group.item(0)
  },

  getText: function (group) {
    return group.item(1)
  },

  editText: function (group, canvas, ignoreKey, doneCallback) {
    const shape = this.getShape(group)
    const text = this.getText(group)

    const hadText = text.text.length !== 0

    this.lockObject(shape)
    this.lockObject(group)

    text.on('editing:exited', () => {
      text.off('changed')
      text.off('editing:exited')

      canvas.remove(text)

      if (text.text.length === 0 && !hadText) {
        return doneCallback(false)
      }

      text.set({
        left: shape.left + shape.strokeWidth + textPadding,
        width: shape.width - (2 * shape.strokeWidth) - (2 * textPadding),
        top: shape.top + (shape.height / 2),
        angle: 0
      })

      group.add(text)

      this.lockObject(shape, false)
      this.lockObject(group, false)

      if (doneCallback) {
        doneCallback(text.text.length !== 0)
      }
    })

    // if the group is new, mark the text to prevent it from persisting
    text.isNew = group.key === ignoreKey
    text.parent = group

    group.realizeTransform(text)
    group.remove(text)

    text.key = ignoreKey

    canvas.add(text)

    // move the selection to the end of the text
    text.setSelectionStart(text.text.length)
    text.setSelectionEnd(text.text.length)

    canvas.setActiveObject(text)
    text.enterEditing()

    // fix for issue where you can no longer enter text
    // not clear why this fixes the issue
    if (text.hiddenTextarea) {
      text.hiddenTextarea.style.lineHeight = 'initial'
    }

    // fix for text disappearing when first editing
    text.dirty = true
  },

  isEmpty: function (group) {
    return this.getText(group).text.length === 0
  },

  setTextColour: function (group, colour) {
    this.getText(group).setColor(colour)
  },

  setShapeColour: function (group, colour) {
    this.getShape(group).setColor(colour)
  },

  setShapeStroke: function (group, colour) {
    this.getShape(group).set({ stroke: colour })
  },

  setShapeStrokeWidth: function (group, width) {
    const shape = this.getShape(group)

    group.set({
      width: shape.width + width,
      height: shape.height + width
    })

    shape.set({
      left: -group.width / 2,
      top: -group.height / 2,
      strokeWidth: width
    })

    const text = this.getText(group)
    if (text) { // won't have text when editting
      text.set({
        left: shape.left + width + textPadding,
        width: shape.width - (2 * width) - (2 * textPadding)
      })
    }
  },

  scale: function (group) {
    const width = group.getScaledWidth()
    const height = group.getScaledHeight()

    group.set({
      width: width,
      height: height,
      scaleX: 1,
      scaleY: 1
    })

    const shape = this.getShape(group)

    if (shape.type === 'ellipse') {
      shape.set({
        left: -width / 2,
        top: -height / 2,
        rx: (width - shape.strokeWidth) / 2,
        ry: (height - shape.strokeWidth) / 2
      })
    } else {
      shape.set({
        left: -width / 2,
        top: -height / 2,
        width: width - shape.strokeWidth,
        height: height - shape.strokeWidth
      })
    }

    const text = this.getText(group)
    text.set({
      left: shape.left + shape.strokeWidth + textPadding,
      width: shape.width - (2 * shape.strokeWidth) - (2 * textPadding),
      top: shape.top + (shape.height / 2)
    })
  },

  lockObject: function (object, lock = true) {
    return object.set({
      selectable: !lock,
      evented: !lock,
      hasControls: !lock,
      hasBorders: !lock,
      lockMovementX: lock,
      lockMovementY: lock,
      lockScalingX: lock,
      lockScalingY: lock,
      lockUniScaling: lock,
      lockRotation: lock
    })
  }
}
